import { ENEMIES } from '../data/enemies';
import { INTERCEPT_LEAD_TICKS, enemyEntry } from '../data/intercept';
import { SIM_HZ } from '../data/tuning';
import { EPSILON } from './collision';
import { lockHeight } from './enemies/hitman-plane';
import { viewOf, type LevelRun } from './level-runner';
import type { TickContext } from './tick';

/**
 * INTERCEPT warning (GDD §8.1, issue #83): every fast enemy is announced 1.0 s before it enters the screen,
 * at the altitude it will have. Pure state of the run: the HUD and the radar beep (#100) only read it.
 */
export interface InterceptWarning {
  /** Index of the enemy in `run.enemies`. */
  entityId: number;
  y: number;
  ticksToEntry: number;
}

/** Ticks until the enemy reaches the right edge of the view, at its entry speed (scroll + its own). */
export function ticksToEntry(distance: number, scrollSpeed: number, extraSpeed: number): number {
  return Math.ceil((distance / (scrollSpeed + extraSpeed)) * SIM_HZ - 1e-6);
}

/**
 * End of every tick: recomputes the warnings. They exist only while the plane flies (VUELO): a crash, the
 * end of the level or a respawn cancels them, and the next ones come with the regenerated stretch. Emits
 * `intercept:warning` when one is created and `intercept:cleared` when it ends (the enemy entered or it
 * was cancelled). The pause stops the simulation, so the countdown only advances with ticks.
 */
export function updateIntercepts(run: LevelRun, context: TickContext): void {
  const { right } = viewOf(run);
  const flying = run.core.levelState === 'VUELO';
  const next = new Map<number, InterceptWarning>();
  if (flying) {
    run.enemies.forEach((enemy, entityId) => {
      const entry = enemyEntry(enemy.type, run.level.enemies[enemy.index]?.params);
      if (!entry || entry.announce !== 'intercept' || enemy.x <= right + EPSILON) return;
      const remaining = ticksToEntry(enemy.x - right, run.scrollSpeed, entry.extraSpeed);
      if (remaining <= INTERCEPT_LEAD_TICKS) next.set(entityId, { entityId, y: enemy.y, ticksToEntry: remaining });
    });
    // The hitman plane called by an antenna (EN-22, #105) is announced like any EN-03, from its take-off.
    run.enemies.forEach((enemy, entityId) => {
      const hitman = enemy.antenna?.hitman;
      if (!hitman || hitman.x <= right + EPSILON) return;
      const remaining = ticksToEntry(hitman.x - right, run.scrollSpeed, ENEMIES['EN-03'].extraSpeed);
      if (remaining <= INTERCEPT_LEAD_TICKS) next.set(entityId, { entityId, y: hitman.y, ticksToEntry: remaining });
    });
  }
  const before = new Set(run.intercepts.map((warning) => warning.entityId));
  for (const warning of next.values()) {
    if (!before.has(warning.entityId)) {
      // The hitman plane takes off now, at the height the pilot has at the warning (issue #85).
      const enemy = run.enemies[warning.entityId]!;
      if (enemy.type === 'EN-03' && !enemy.launched) {
        enemy.targetY = lockHeight(run.core.player.y);
        enemy.y = enemy.targetY;
        enemy.launched = true;
        warning.y = enemy.targetY;
      }
      context.emit({ type: 'intercept:warning', entityId: warning.entityId, y: warning.y });
    }
  }
  for (const entityId of before) {
    if (!next.has(entityId)) context.emit({ type: 'intercept:cleared', entityId });
  }
  run.intercepts = [...next.values()];
}
